import React, { useContext, useEffect, useState } from "react";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import ViewIcon from "@material-ui/icons/Visibility";
import CustomTable, { CustomTableRowData } from "../../../components/CustomTable";
import { ProductContext } from "../../../contexts/ProductContext";
import formatIdentifierAsString from "../../../api/services/common/formatIdentifierAsString";
import { WithPredefinedChip } from "../../../components/Chip/PredefinedSampleChip";
import { ProductSampleReference, TesaPrototypeSample } from "../../../api/models/product/ProductSampleReference";
import ProductSampleDTO from "../../../api/models/product/ProductSampleDTO";

function isTesaPrototypeSampleReference(
  sampleRef: ProductSampleReference,
): sampleRef is ProductSampleReference<TesaPrototypeSample> {
  return sampleRef.productSample.type === "TESA_PROTOTYPE";
}
function isPrototypeSample(sample: ProductSampleDTO): sample is TesaPrototypeSample {
  return sample.type === "TESA_PROTOTYPE";
}
interface TesaPrototypeSampleOverviewTableProps {
  onDelete: (competitorProductSample: Partial<TesaPrototypeSample>) => void;
  onEdit: (
    productSampleId: number,
    additionalActions?: (sampleData: ProductSampleDTO, usedIn: boolean) => void,
  ) => void;
  productSampleReferences: ProductSampleReference[];
  setApiRequestType: (reqType: string) => void;
  showProductSampleDeleteAction: (data: { id: number }) => boolean;
  showProductSampleEditAction: (data: { id: number }) => boolean;
  showProductSampleViewDetailsAction: (data: { id: number }) => boolean;
}

const TesaPrototypeSampleOverviewTable: React.FC<TesaPrototypeSampleOverviewTableProps> = (props) => {
  const {
    onDelete,
    onEdit: handleEditById,
    productSampleReferences,
    setApiRequestType,
    showProductSampleDeleteAction,
    showProductSampleEditAction,
    showProductSampleViewDetailsAction,
  } = props;
  const [tesaPrototypeSampleReferences, setTesaPrototypeSampleReferences] = useState<
    ProductSampleReference<TesaPrototypeSample>[]
  >([]);
  const { updatePrototypeObject } = useContext(ProductContext);

  const onEditAdditionalActions = (sampleData: ProductSampleDTO, usedIn: boolean) => {
    if (isPrototypeSample(sampleData)) {
      updatePrototypeObject({
        ...sampleData,
        usedIn,
      });
      setApiRequestType("PUT");
    }
  };

  const handleEdit = (rowData: CustomTableRowData) => {
    if (showProductSampleEditAction(rowData) || showProductSampleViewDetailsAction(rowData)) {
      handleEditById(rowData.id, onEditAdditionalActions);
    }
  };

  useEffect(() => {
    const newSampleRefs = productSampleReferences.filter(
      (psr: ProductSampleReference): psr is ProductSampleReference<TesaPrototypeSample> =>
        isTesaPrototypeSampleReference(psr),
    );
    if (JSON.stringify(newSampleRefs) !== JSON.stringify(tesaPrototypeSampleReferences)) {
      setTesaPrototypeSampleReferences(newSampleRefs);
    }
  }, [productSampleReferences]);

  if (tesaPrototypeSampleReferences.length === 0) {
    return <></>;
  }
  return (
    <CustomTable
      boldIndex={0}
      selectedItemNameValue="productSampleName"
      actionColumnIndexes={[0]}
      excludedKeys={["id", "usedIn"]}
      hasRowNumbers
      hasFixedColumns
      handleEdit={handleEdit}
      header="tesa Prototype Samples"
      columns={["Product Sample Name", "Prototype Name", "R&D Responsible", "Delivered by"]}
      rowMenuActions={[
        {
          action: (rowData): void => handleEditById(rowData.id, onEditAdditionalActions),
          icon: <EditIcon />,
          text: "Edit",
          show: showProductSampleEditAction,
        },
        {
          action: (rowData): void => handleEditById(rowData.id, onEditAdditionalActions),
          icon: <ViewIcon />,
          text: "View Details",
          show: (data) => !showProductSampleEditAction(data),
        },
        {
          action: (rowData): void => onDelete(rowData),
          icon: <DeleteIcon />,
          text: "Delete",
          show: showProductSampleDeleteAction,
        },
      ]}
      rowMenus
      rows={tesaPrototypeSampleReferences.map((psr, index: number) => {
        const { usedIn, productSample } = psr;
        return {
          productSampleName: usedIn ? (
            <WithPredefinedChip text={productSample.productSampleName} />
          ) : (
            productSample.productSampleName
          ),
          prototypeName: productSample.prototypeName,
          prototypeDeveloper: productSample.prototypeDeveloper,
          deliveredBy: formatIdentifierAsString(productSample.deliveredBy),
          id: productSample.id,
          rowNumber: index + 1,
          usedIn,
        };
      })}
    />
  );
};

export default TesaPrototypeSampleOverviewTable;
